import type { SelectInputItem } from '@gat-ship/vue-components'

/** Accessors and ordering for {@link toSelectItems}. */
export interface ToSelectItemsOptions<T> {
  /** The combobox value for an item (the bound `model-value`). */
  value: (item: T) => string
  /** The display title for an item. */
  title: (item: T) => string
  /** The searchable string; defaults to `value`. May be `undefined`. */
  search?: (item: T) => string | undefined
  /** Optional sort comparator, applied to a copy of the list. */
  sort?: (a: T, b: T) => number
  /** Optional filter, applied before mapping. */
  filter?: (item: T) => boolean
}

/**
 * Maps a reference-data list to the `SelectInputItem<T>[]` shape the
 * `InputGroupCombobox` pickers feed their dropdowns. Copies before sorting, so
 * it never mutates the shared datasource cache in place (the inline versions
 * did). `metaData` carries the original item for per-picker row rendering.
 */
export function toSelectItems<T>(
  list: readonly T[] | undefined,
  options: ToSelectItemsOptions<T>
): SelectInputItem<T>[] {
  let items = list ? [...list] : []
  if (options.filter) items = items.filter(options.filter)
  if (options.sort) items = items.sort(options.sort)
  const search = options.search ?? options.value
  return items.map((item) => ({
    value: options.value(item),
    title: options.title(item),
    searchValue: search(item),
    metaData: item
  }))
}
